import { UpTransition } from '@components/animations'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'
import VisitForm from '@components/forms/VisitForm'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { VisitDTO } from '@services/dtos'
import { setRecipients } from '@state/reducers/recipientReducer'
import {
  doFindVisits,
  doRemoveVisit,
  setVisitFilters,
} from '@state/reducers/visitReducer'
import { EditIcon, Trash2 } from 'lucide-react'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import VisitTableToolbar from './Toolbars/VisitTableToolbar'

const VisitsTable = () => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { filters, visits } = useAppSelector(({ visits }) => ({
    visits: visits.visits,
    filters: visits.filters,
  }))

  const [isVisitFormOpen, setisVisitFormOpen] = useState(false)
  const [editingVisit, seteditingVisit] = useState<VisitDTO | null>(null)
  const [deletingVisit, setdeletingVisit] = useState<VisitDTO | null>(null)

  useEffect(() => {
    dispatch(doFindVisits())
  }, [filters])

  useEffect(() => {
    return () => {
      dispatch(setRecipients([]))
    }
  }, [])

  const columns = useMemo(() => {
    const d: GridColumns = [
      {
        field: 'identifier',
        headerName: 'ID',
        flex: 1,
        width: 100,
      },
      {
        field: 'createdAt',
        headerName: intl.formatMessage({ id: 'createdAt' }),
        width: 160,
        flex: 1,
        renderCell: (v) => {
          return moment(v.value).format('DD/MM/YYYY HH:mm')
        },
      },
      {
        field: 'patient.firstName',
        headerName: intl.formatMessage({ id: 'firstName' }),
        width: 160,
        flex: 1,
        renderCell: (params) => params.row.patient.firstName,
      },
      {
        field: 'patient.lastName',
        headerName: intl.formatMessage({ id: 'lastName' }),
        width: 160,
        flex: 1,
        renderCell: (params) => params.row.patient.lastName,
      },
      {
        field: 'site',
        headerName: 'Site',
        width: 160,
        flex: 1,
        renderCell: (params) => (
          <span>
            {params.row.site?.label} ({params.row.site?.code})
          </span>
        ),
      },
      {
        field: 'medicalFunctionalUnit',
        headerName: intl.formatMessage({ id: 'medicalFunctionalUnit' }),
        flex: 1,
        width: 230,
        renderCell: (params) => {
          const unit = params.row.medicalFunctionalUnit
          if (!unit) return '-'

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography variant="body2" fontWeight="medium">
                {unit.name}
              </Typography>
              {unit.functionType && (
                <Typography variant="caption" color="text.secondary">
                  {unit.functionType}
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        field: 'accomodationFunctionalUnit',
        headerName: intl.formatMessage({ id: 'accomodationFunctionalUnit' }),
        flex: 1,
        width: 250,
        renderCell: (params) => {
          const unit = params.row.accomodationFunctionalUnit
          if (!unit) return '-'

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography variant="body2" fontWeight="medium">
                {unit.name}
              </Typography>
              {unit.functionType && (
                <Typography variant="caption" color="text.secondary">
                  {unit.functionType}
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        field: 'admissionDate',
        headerName: intl.formatMessage({ id: 'admissionDate' }),
        flex: 1,
        width: 160,
        valueGetter: (params) => {
          return params.row.admissionDate
            ? moment(params.row.admissionDate).format('DD/MM/YYYY')
            : '-'
        },
      },
      {
        field: 'dischargedDate',
        headerName: intl.formatMessage({ id: 'dischargedDate' }),
        flex: 1,
        width: 140,
        valueGetter: (params) => {
          return params.row.dischargedDate
            ? moment(params.row.dischargedDate).format('DD/MM/YYYY')
            : '-'
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        align: 'right',
        pinnable: false,
        renderCell: (params) => {
          return (
            <div className="flex items-center justify-center">
              <IconButton
                size="small"
                onClick={() => {
                  seteditingVisit(params.row)
                  setisVisitFormOpen(true)
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setdeletingVisit(params.row)
                }}
              >
                <Trash2 />
              </IconButton>
            </div>
          )
        },
      },
    ]
    return d
  }, [intl])

  const closeVisitForm = () => {
    setisVisitFormOpen(false)
    seteditingVisit(null)
  }

  return (
    <div className="h-[calc(100vh-130px)]">
      <DataGridPro
        sx={{
          display: 'flex',
          gap: 2,
          padding: '16px',
          backgroundColor: 'white',
          borderRadius: '16px',
        }}
        rows={visits.datas}
        columns={columns}
        paginationMode="server"
        pageSize={filters.limit}
        rowCount={visits.totalCount}
        initialState={{
          pinnedColumns: { right: ['actions'] },
        }}
        onPageChange={(page) => {
          dispatch(
            setVisitFilters({
              page,
            }),
          )
        }}
        components={{
          Toolbar: VisitTableToolbar,
        }}
        componentsProps={{
          toolbar: {
            onAddVisit: () => {
              setisVisitFormOpen(true)
            },
          },
        }}
        pagination
      />
      <Dialog
        fullScreen
        open={isVisitFormOpen}
        onClose={closeVisitForm}
        TransitionComponent={UpTransition}
        slotProps={{
          backdrop: {},
        }}
      >
        <DialogContent>
          {isVisitFormOpen && (
            <VisitForm
              onClose={() => {
                setisVisitFormOpen(false)
                seteditingVisit(null)
              }}
              editingVisit={editingVisit}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        title="Suppression"
        severity="error"
        message="Voulez-vous vraiment supprimer cette visite ?"
        open={deletingVisit !== null}
        onConfirm={() => {
          if (deletingVisit) {
            dispatch(doRemoveVisit(deletingVisit.id))
            setdeletingVisit(null)
          }
        }}
        onCancel={() => {
          setdeletingVisit(null)
        }}
      />
    </div>
  )
}

export default VisitsTable
